import { cn } from '../../../components/ui/utils';

export interface BarListItem {
  key: string;
  label: string;
  value: number;
  color?: string;
  hint?: string;
}

export function HrBarList({
  items,
  max,
  formatter = (value) => String(value),
  onSelect,
  selectedKeys = [],
  unit,
  emptyLabel = 'No records in the current filter',
}: {
  items: BarListItem[];
  max?: number;
  formatter?: (value: number) => string;
  onSelect?: (key: string) => void;
  selectedKeys?: string[];
  unit?: string;
  emptyLabel?: string;
}) {
  if (items.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">{emptyLabel}</p>;
  }

  const ceiling = max || Math.max(1, ...items.map((item) => item.value));
  const hasSelection = selectedKeys.length > 0;

  return (
    <div className="space-y-2">
      {items.map((item) => {
        const selected = selectedKeys.includes(item.key);
        const dimmed = hasSelection && !selected;
        const width = item.value > 0 ? Math.max(1, (item.value / ceiling) * 100) : 0;

        const content = (
          <>
            <span
              className={cn(
                'truncate text-sm',
                selected ? 'font-medium text-link' : 'text-foreground',
                dimmed && 'opacity-50'
              )}
              title={item.label}
            >
              {item.label}
            </span>
            <span className="h-4 overflow-hidden rounded-sm bg-muted">
              <span
                className={cn('block h-full rounded-sm transition-all', dimmed && 'opacity-40')}
                style={{
                  width: `${width}%`,
                  backgroundColor: item.color || 'var(--chart-3)',
                }}
              />
            </span>
            <span className="text-right text-sm font-medium tabular-nums text-foreground">
              {formatter(item.value)}
              {unit ? <span className="ml-0.5 font-normal text-muted-foreground">{unit}</span> : null}
            </span>
          </>
        );

        return onSelect ? (
          <button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            title={item.hint}
            className="grid w-full grid-cols-[minmax(0,1fr)_minmax(60px,1.6fr)_auto] items-center gap-3 rounded-md px-1 py-1 text-left transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {content}
          </button>
        ) : (
          <div
            key={item.key}
            title={item.hint}
            className="grid grid-cols-[minmax(0,1fr)_minmax(60px,1.6fr)_auto] items-center gap-3 px-1 py-1"
          >
            {content}
          </div>
        );
      })}
    </div>
  );
}
